/* =========================
   Animations + Read More
   ========================= */

const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

/**
 * Throttle helper (Dawn-style)
 * Prevents a function from running too often on scroll.
 */
function throttle(fn, wait = 100) {
  let lastTime = 0;
  let timeoutId = null;

  return function throttled(...args) {
    const now = Date.now();
    const remaining = wait - (now - lastTime);

    if (remaining <= 0) {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      lastTime = now;
      fn.apply(this, args);
      return;
    }

    if (!timeoutId) {
      timeoutId = setTimeout(() => {
        lastTime = Date.now();
        timeoutId = null;
        fn.apply(this, args);
      }, remaining);
    }
  };
}

/* =========================
   Scroll-in animation logic
   ========================= */
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;

      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);

        if (elementTarget.hasAttribute('data-cascade')) {
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
        }
      }

      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });

  animationTriggerElements.forEach((element) => observer.observe(element));
}

/* =========================
   Zoom-in animation logic
   ========================= */
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });

    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;
        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }, 50),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) return 0; // not reached yet
  if (elementPositionY + elementHeight < scrollY) return 100; // passed fully

  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);

  return Math.round(percentage);
}

/* =========================
   Bundle/Product description: Read more / Read less
   ========================= */
function setupReadMore() {
  // Try the most reliable container first (Dawn product description block)
  const root =
    document.querySelector('.product__description') ||
    document.querySelector('.product__description .rte') ||
    document.querySelector('.product__description.rte');

  if (!root) return;

  // Make sure we clamp the actual text element (prefer .rte if it exists)
  const desc = root.classList.contains('rte') ? root : root.querySelector('.rte') || root;

  // Prevent duplicates (theme editor reload, ajax nav, etc.)
  if (desc.dataset.readmoreInit === 'true') return;
  desc.dataset.readmoreInit = 'true';

  // If a button already exists nearby, don’t add again
  const existingBtn = desc.parentElement?.querySelector('.bundle-read-more');
  if (existingBtn) existingBtn.remove();

  const text = (desc.innerText || '').trim();
  if (text.length < 220) return; // change threshold if you want

  // Clamp ON by default
  desc.classList.add('is-clamped');

  // Button
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'bundle-read-more';
  btn.setAttribute('aria-expanded', 'false');
  btn.textContent = 'Read more';

  btn.addEventListener('click', () => {
    const clamped = desc.classList.toggle('is-clamped');
    btn.textContent = clamped ? 'Read more' : 'Read less';
    btn.setAttribute('aria-expanded', clamped ? 'false' : 'true');
  });

  // Insert right under the description (inside the same block if possible)
  desc.insertAdjacentElement('afterend', btn);
}

/* =========================
   Init
   ========================= */
window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
  setupReadMore();
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
    setupReadMore();
  });
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
window.addEventListener('DOMContentLoaded', () => {
  setupReadMore();
});

document.addEventListener('shopify:section:load', setupReadMore);
document.addEventListener('shopify:section:reorder', setupReadMore);
(function () {
  function findDescriptionEl() {
    // Dawn commonly uses one of these
    return (
      document.querySelector('.product__description .rte') ||
      document.querySelector('.product__description') ||
      document.querySelector('[data-product-description]') ||
      null
    );
  }

  function setupReadMore() {
    const desc = findDescriptionEl();
    if (!desc) return false;

    // avoid duplicates
    if (desc.dataset.readmoreInit === 'true') return true;
    desc.dataset.readmoreInit = 'true';

    // remove existing button if any (safety)
    const oldBtn =
      desc.parentElement?.querySelector('.bundle-read-more') ||
      desc.nextElementSibling?.classList?.contains('bundle-read-more')
        ? desc.nextElementSibling
        : null;
    if (oldBtn) oldBtn.remove();

    // Apply clamp first so we can measure overflow
    desc.classList.add('is-clamped');

    // If it doesn't overflow, undo clamp and stop
    // (small timeout allows layout to calculate heights)
    setTimeout(() => {
      const isOverflowing = desc.scrollHeight > desc.clientHeight + 2;
      if (!isOverflowing) {
        desc.classList.remove('is-clamped');
        return;
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundle-read-more';
      btn.textContent = 'Read more';
      btn.setAttribute('aria-expanded', 'false');

      btn.addEventListener('click', () => {
        const nowClamped = desc.classList.toggle('is-clamped');
        btn.textContent = nowClamped ? 'Read more' : 'Read less';
        btn.setAttribute('aria-expanded', nowClamped ? 'false' : 'true');
      });

      desc.insertAdjacentElement('afterend', btn);
    }, 0);

    return true;
  }

  // Run once on load
  document.addEventListener('DOMContentLoaded', () => {
    // Try immediately
    if (setupReadMore()) return;

    // If the product section loads late, watch briefly
    const mo = new MutationObserver(() => {
      if (setupReadMore()) mo.disconnect();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });

    // safety stop
    setTimeout(() => mo.disconnect(), 5000);
  });

  // Theme editor events
  document.addEventListener('shopify:section:load', setupReadMore);
  document.addEventListener('shopify:section:reorder', setupReadMore);
})();

/* Fix multicolumn mobile slider counter (0/2 -> 1/3) */
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.multicolumn slider-component');

  sliders.forEach(slider => {
    const counterCurrent = slider.querySelector('.slider-counter--current');
    const counterTotal = slider.querySelector('.slider-counter--total');
    const slides = slider.querySelectorAll('.slider__slide');

    if (!counterCurrent || !counterTotal || slides.length === 0) return;

    // Force correct total
    counterTotal.textContent = slides.length;

    // Make sure current never shows 0
    if (counterCurrent.textContent === '0') {
      counterCurrent.textContent = '1';
    }

    // Update on slide change
    slider.addEventListener('scroll', () => {
      const slideWidth = slides[0].offsetWidth;
      const index = Math.round(slider.scrollLeft / slideWidth) + 1;
      counterCurrent.textContent = Math.min(index, slides.length);
    });
  });
});
